import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { Monitor, Plus, Pencil, Trash2, RefreshCw, Wifi, WifiOff, LayoutGrid, MapPin, ArrowLeft, Play, ExternalLink } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SCREEN_TYPES = [
  { value: "kiosk", label: "Kiosk" },
  { value: "digital_signage", label: "Digital Signage" },
  { value: "display_board", label: "Display Board" }
];

const ORIENTATIONS = [
  { value: "landscape", label: "Landscape" },
  { value: "portrait", label: "Portrait" }
];

const RESOLUTIONS = [
  { value: "1920x1080", label: "1920x1080 (Full HD)" },
  { value: "1080x1920", label: "1080x1920 (Portrait Full HD)" },
  { value: "3840x2160", label: "3840x2160 (4K)" },
  { value: "1280x720", label: "1280x720 (HD)" }
];

export default function AdminScreens() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [screens, setScreens] = useState([]);
  const [sites, setSites] = useState([]);
  const [stats, setStats] = useState(null);
  const [filterSite, setFilterSite] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [showInactive, setShowInactive] = useState(false);
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingScreen, setEditingScreen] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    site_id: "",
    screen_type: "kiosk",
    orientation: "landscape",
    resolution: "1920x1080",
    location_description: ""
  });

  const checkAuth = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
        return true;
      }
    } catch (err) {
      console.error("Auth check failed:", err);
    }
    navigate("/admin/login");
    return false;
  }, [navigate]);

  const fetchSites = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sites`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setSites(data);
      }
    } catch (err) {
      console.error("Failed to fetch sites:", err);
    }
  };

  const fetchScreens = useCallback(async () => {
    try {
      let url = `${API_URL}/api/screens?include_inactive=${showInactive}`;
      if (filterSite !== "all") url += `&site_id=${filterSite}`;
      if (filterType !== "all") url += `&screen_type=${filterType}`;
      
      const res = await fetch(url, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setScreens(data);
      }
    } catch (err) {
      console.error("Failed to fetch screens:", err);
    }
  }, [showInactive, filterSite, filterType]);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/screens/stats/summary`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (err) {
      console.error("Failed to fetch stats:", err);
    }
  };

  useEffect(() => {
    const init = async () => {
      const authed = await checkAuth();
      if (authed) {
        setLoading(false);
        await Promise.all([fetchSites(), fetchScreens(), fetchStats()]);
      }
    };
    init();
  }, [checkAuth, fetchScreens]);

  useEffect(() => {
    if (user) {
      fetchScreens();
    }
  }, [filterSite, filterType, showInactive, user, fetchScreens]);

  const handleCreate = async () => {
    if (!formData.name || !formData.site_id) {
      toast.error("Name and site are required");
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/screens`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        toast.success("Screen created successfully");
        setShowCreateDialog(false);
        resetForm();
        fetchScreens();
        fetchStats();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to create screen");
      }
    } catch (err) {
      toast.error("Failed to create screen");
    }
  };

  const handleUpdate = async () => {
    if (!editingScreen) return;
    
    try {
      const res = await fetch(`${API_URL}/api/screens/${editingScreen.screen_id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        toast.success("Screen updated successfully");
        setShowEditDialog(false);
        setEditingScreen(null);
        resetForm();
        fetchScreens();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to update screen");
      }
    } catch (err) {
      toast.error("Failed to update screen");
    }
  };

  const handleDeactivate = async (screenId) => {
    if (!window.confirm("Are you sure you want to deactivate this screen?")) return;
    
    try {
      const res = await fetch(`${API_URL}/api/screens/${screenId}`, {
        method: "DELETE",
        credentials: "include"
      });
      
      if (res.ok) {
        toast.success("Screen deactivated");
        fetchScreens();
        fetchStats();
      } else {
        toast.error("Failed to deactivate screen");
      }
    } catch (err) {
      toast.error("Failed to deactivate screen");
    }
  };

  const openEditDialog = (screen) => {
    setEditingScreen(screen);
    setFormData({
      name: screen.name,
      site_id: screen.site_id,
      screen_type: screen.screen_type,
      orientation: screen.orientation,
      resolution: screen.resolution || "1920x1080",
      location_description: screen.location_description || ""
    });
    setShowEditDialog(true);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      site_id: "",
      screen_type: "kiosk",
      orientation: "landscape",
      resolution: "1920x1080",
      location_description: ""
    });
  };

  const getSiteName = (siteId) => {
    const site = sites.find(s => s.site_id === siteId);
    return site?.name || siteId;
  };

  const isOnline = (lastHeartbeat) => {
    if (!lastHeartbeat) return false;
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
    return new Date(lastHeartbeat) > fiveMinutesAgo;
  };

  const getScreenTypeLabel = (type) => {
    const found = SCREEN_TYPES.find(t => t.value === type);
    return found?.label || type;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/admin")}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Monitor className="h-6 w-6 text-emerald-400" />
                Screen Management
              </h1>
              <p className="text-sm text-slate-400">Manage digital screens and kiosks</p>
            </div>
          </div>
          <Button onClick={() => setShowCreateDialog(true)} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="h-4 w-4 mr-2" />
            Add Screen
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <Monitor className="h-8 w-8 text-blue-400" />
                  <div>
                    <p className="text-2xl font-bold">{stats.total_screens}</p>
                    <p className="text-sm text-slate-400">Total Screens</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <Wifi className="h-8 w-8 text-emerald-400" />
                  <div>
                    <p className="text-2xl font-bold">{stats.online_screens}</p>
                    <p className="text-sm text-slate-400">Online</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <WifiOff className="h-8 w-8 text-red-400" />
                  <div>
                    <p className="text-2xl font-bold">{stats.offline_screens}</p>
                    <p className="text-sm text-slate-400">Offline</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <LayoutGrid className="h-8 w-8 text-purple-400" />
                  <div>
                    <p className="text-2xl font-bold">{Object.keys(stats.by_type || {}).length}</p>
                    <p className="text-sm text-slate-400">Screen Types</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <Select value={filterSite} onValueChange={setFilterSite}>
            <SelectTrigger className="w-48 bg-slate-800 border-slate-700">
              <SelectValue placeholder="Filter by site" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sites</SelectItem>
              {sites.map(site => (
                <SelectItem key={site.site_id} value={site.site_id}>{site.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-48 bg-slate-800 border-slate-700">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {SCREEN_TYPES.map(type => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button
            variant={showInactive ? "default" : "outline"}
            onClick={() => setShowInactive(!showInactive)}
            className={showInactive ? "bg-amber-600" : ""}
          >
            {showInactive ? "Showing Inactive" : "Show Inactive"}
          </Button>
          
          <Button variant="outline" onClick={() => { fetchScreens(); fetchStats(); }}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Screens Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {screens.map(screen => (
            <Card key={screen.screen_id} className={`bg-slate-800 border-slate-700 ${!screen.is_active ? 'opacity-60' : ''}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      {screen.name}
                      {isOnline(screen.last_heartbeat) ? (
                        <Wifi className="h-4 w-4 text-emerald-400" />
                      ) : (
                        <WifiOff className="h-4 w-4 text-slate-500" />
                      )}
                    </CardTitle>
                    <CardDescription className="flex items-center gap-1 mt-1">
                      <MapPin className="h-3 w-3" />
                      {getSiteName(screen.site_id)}
                    </CardDescription>
                  </div>
                  <Badge variant={screen.is_active ? "default" : "secondary"}>
                    {screen.is_active ? "Active" : "Inactive"}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Type:</span>
                    <span>{getScreenTypeLabel(screen.screen_type)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Resolution:</span>
                    <span>{screen.resolution || "1920x1080"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Orientation:</span>
                    <span className="capitalize">{screen.orientation}</span>
                  </div>
                  {screen.location_description && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Location:</span>
                      <span className="text-right max-w-32 truncate">{screen.location_description}</span>
                    </div>
                  )}
                  {screen.last_heartbeat && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Last Seen:</span>
                      <span>{new Date(screen.last_heartbeat).toLocaleString()}</span>
                    </div>
                  )}
                </div>
                
                <div className="flex gap-2 mt-4">
                  <Button size="sm" variant="outline" onClick={() => window.open(`/player/${screen.screen_id}`, '_blank')}>
                    <Play className="h-3 w-3 mr-1" />
                    Player
                  </Button>
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => openEditDialog(screen)}>
                    <Pencil className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                  {screen.is_active && (
                    <Button size="sm" variant="destructive" onClick={() => handleDeactivate(screen.screen_id)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {screens.length === 0 && (
          <div className="text-center py-12 text-slate-400">
            <Monitor className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No screens found</p>
            <p className="text-sm">Create a screen to get started</p>
          </div>
        )}
      </main>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-slate-800 text-white border-slate-700">
          <DialogHeader>
            <DialogTitle>Add New Screen</DialogTitle>
            <DialogDescription>Create a new screen for digital signage</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Screen Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Main Entrance Kiosk"
                className="bg-slate-700 border-slate-600"
              />
            </div>
            
            <div>
              <Label>Site *</Label>
              <Select value={formData.site_id} onValueChange={(v) => setFormData({ ...formData, site_id: v })}>
                <SelectTrigger className="bg-slate-700 border-slate-600">
                  <SelectValue placeholder="Select site" />
                </SelectTrigger>
                <SelectContent>
                  {sites.map(site => (
                    <SelectItem key={site.site_id} value={site.site_id}>{site.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Screen Type</Label>
              <Select value={formData.screen_type} onValueChange={(v) => setFormData({ ...formData, screen_type: v })}>
                <SelectTrigger className="bg-slate-700 border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SCREEN_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Orientation</Label>
                <Select value={formData.orientation} onValueChange={(v) => setFormData({ ...formData, orientation: v })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ORIENTATIONS.map(o => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Resolution</Label>
                <Select value={formData.resolution} onValueChange={(v) => setFormData({ ...formData, resolution: v })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RESOLUTIONS.map(r => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Location Description</Label>
              <Input
                value={formData.location_description}
                onChange={(e) => setFormData({ ...formData, location_description: e.target.value })}
                placeholder="e.g., Near main entrance, left side"
                className="bg-slate-700 border-slate-600"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }}>Cancel</Button>
            <Button onClick={handleCreate} className="bg-emerald-600 hover:bg-emerald-700">Create Screen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-slate-800 text-white border-slate-700">
          <DialogHeader>
            <DialogTitle>Edit Screen</DialogTitle>
            <DialogDescription>Update screen configuration</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Screen Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-slate-700 border-slate-600"
              />
            </div>
            
            <div>
              <Label>Site *</Label>
              <Select value={formData.site_id} onValueChange={(v) => setFormData({ ...formData, site_id: v })}>
                <SelectTrigger className="bg-slate-700 border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sites.map(site => (
                    <SelectItem key={site.site_id} value={site.site_id}>{site.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Screen Type</Label>
              <Select value={formData.screen_type} onValueChange={(v) => setFormData({ ...formData, screen_type: v })}>
                <SelectTrigger className="bg-slate-700 border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SCREEN_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Orientation</Label>
                <Select value={formData.orientation} onValueChange={(v) => setFormData({ ...formData, orientation: v })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ORIENTATIONS.map(o => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Resolution</Label>
                <Select value={formData.resolution} onValueChange={(v) => setFormData({ ...formData, resolution: v })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RESOLUTIONS.map(r => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Location Description</Label>
              <Input
                value={formData.location_description}
                onChange={(e) => setFormData({ ...formData, location_description: e.target.value })}
                className="bg-slate-700 border-slate-600"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditDialog(false); setEditingScreen(null); resetForm(); }}>Cancel</Button>
            <Button onClick={handleUpdate} className="bg-emerald-600 hover:bg-emerald-700">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
